import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Shimmer from "src/components-v2/Shimmer";

import { BankObject, BeneficiaryBanksGroupsResponse } from "src/api/interfaces/general";
import {
  BIGFLIP_DEPOSIT,
  FFB_COA,
  HIDDEN_VA_BANKS,
  OVERRIDE_BANKS_NAME,
  REMOVED_TRANSACTION_BANKS,
  REMOVED_TRANSACTION_BANKS_HISTORY
} from "src/consts/banks";
import { useSelector } from "./state";

function getBankList(bankObject: Partial<BeneficiaryBanksGroupsResponse>) {
  const bankList: { [bankCode: string]: { key: string; name: string } } = {};
  for (const value of Object.values(bankObject)) {
    value.forEach((item) => {
      bankList[item.bank_code] = {
        key: item.bank_code,
        name: item.bank_name
      };
    });
  }
  return bankList;
}

/*
 * Hook that provides functions to get the bank name
 *
 * Usage:
 *
 * const { getBankName } = useBankName();
 * <p>{getBankName("bca")}</p>
 */
export function useBankName() {
  const { t } = useTranslation("translation");
  const { beneficiaryBanks, loadingBeneficiaryBanks } = useSelector((state) => state.commonBank);

  const bankList = getBankList({ ...beneficiaryBanks, ...BIGFLIP_DEPOSIT, ...FFB_COA });
  const undefinedAlert = <span className="u-text-dark-grey u-text-small u-italic">{t("bank-name.undefined")}</span>;

  function getBankName(bankCode: string, isVA: boolean = false) {
    const noBeneficiaryBank = Object.keys(beneficiaryBanks).length === 0 || loadingBeneficiaryBanks;
    if (noBeneficiaryBank) return <Shimmer height={14} width="100%" />;

    const bank = bankList[bankCode];
    return bank ? `${bank.name}${isVA ? " VA" : ""}` : undefinedAlert;
  }

  function getSafelyBankName(bankCode: string) {
    const noBeneficiaryBank = Object.keys(beneficiaryBanks).length === 0 || loadingBeneficiaryBanks;
    if (noBeneficiaryBank) return "";

    const bank = bankList[bankCode];
    return bank ? bank.name : "";
  }

  return {
    getBankName,
    getSafelyBankName
  };
}

/*
 * Hook that provides bank options for dropdown
 *
 * Usage:
 *
 * const banksOptions = useBankOption();
 */
export function useBankOption({ bankTypes = ["bank_account", "e_wallet"] as const, showAll = false } = {}) {
  const { beneficiaryBanks, loadingBeneficiaryBanks } = useSelector((state) => state.commonBank);
  if (Object.keys(beneficiaryBanks).length === 0 || loadingBeneficiaryBanks) return [];

  const bankObject = {} as Partial<BeneficiaryBanksGroupsResponse>;
  bankTypes.forEach((type) => (bankObject[type] = beneficiaryBanks[type]));
  const bankList = getBankList(bankObject);
  const filterDeletedBanks = showAll ? REMOVED_TRANSACTION_BANKS_HISTORY : REMOVED_TRANSACTION_BANKS;

  filterDeletedBanks.forEach((bankKey) => {
    delete bankList[bankKey];
  });

  // Override bank names
  if (OVERRIDE_BANKS_NAME && Array.isArray(OVERRIDE_BANKS_NAME)) {
    OVERRIDE_BANKS_NAME.forEach(({ bank_code, bank_name }) => {
      if (bankList[bank_code]) {
        bankList[bank_code].name = bank_name;
      }
    });
  }

  return Object.values(bankList).map((bank) => ({
    label: bank.name,
    bankLabel: (
      <div className="u-flex u-items-center">
        <span className={`c-bank-logo c-bank-logo--tiny c-bank-logo--${bank.key} u-mr-2`} />
        <span>{bank.name}</span>
      </div>
    ),
    value: bank.key
  }));
}

/*
 * Hook that provides list of banks/vas/ewallets that's already filtered
 *
 * Usage:
 *
 * const { banks, virtualAccounts, ewallets } = useGetListBanks();
 */
export function useGetListBanks({ showAll = false } = {}) {
  const [state, setState] = useState<{
    banks: BankObject[];
    virtualAccounts: BankObject[];
    ewallets: BankObject[];
  }>({
    banks: [],
    virtualAccounts: [],
    ewallets: []
  });
  const { beneficiaryBanks, loadingBeneficiaryBanks } = useSelector((state) => state.commonBank);

  useEffect(() => {
    if (!loadingBeneficiaryBanks && Object.keys(beneficiaryBanks).length > 0) {
      let banks =
        beneficiaryBanks?.bank_account
          ?.filter(({ bank_code }) =>
            showAll
              ? !REMOVED_TRANSACTION_BANKS_HISTORY.includes(bank_code)
              : !REMOVED_TRANSACTION_BANKS.includes(bank_code)
          )
          .filter(({ bank_code }) => !bank_code.endsWith("_va")) || // workaround to hide the VA in 'bank_accounts' property
        [];

      banks = banks.map((bank) => {
        if (OVERRIDE_BANKS_NAME && Array.isArray(OVERRIDE_BANKS_NAME)) {
          const overrideBank = OVERRIDE_BANKS_NAME.find(({ bank_code }) => bank_code === bank.bank_code);
          if (overrideBank) {
            return {
              ...bank,
              bank_name: overrideBank.bank_name
            };
          }
        }
        return bank;
      });

      const virtualAccounts = beneficiaryBanks?.virtual_account?.filter(({ bank_code }) =>
        showAll ? true : !HIDDEN_VA_BANKS.includes(bank_code)
      );

      const ewallets = beneficiaryBanks?.e_wallet?.filter(({ bank_code }) =>
        showAll
          ? !REMOVED_TRANSACTION_BANKS_HISTORY.includes(bank_code)
          : !REMOVED_TRANSACTION_BANKS.includes(bank_code)
      );

      setState({
        banks,
        virtualAccounts,
        ewallets
      });
    }
  }, [beneficiaryBanks]);

  return state;
}
